"use client"

import { useEffect } from "react"

import { sendGTMEventIfAnalyticsAllowed } from "@/lib/ga4TrackingHelper"
import { sanitizeAnalyticsPath, sanitizeAnalyticsUrl } from "@/lib/tracking/url-sanitizer"

const trackingSelector = "[data-gtm-tracking-id]"
const interactiveSelector = [
  "a[href]",
  "button",
  "input[type='button']",
  "input[type='submit']",
  "input[type='reset']",
  "[role='button']",
  "[role='link']",
  "[data-track-click]",
].join(",")

function findElement(target: EventTarget | null, selector: string) {
  return target instanceof Element ? target.closest<HTMLElement>(selector) : null
}

function getElementLabel(element: HTMLElement | null) {
  if (!element) return undefined

  return (
    element.getAttribute("aria-label") ||
    element.getAttribute("title") ||
    element.textContent?.replace(/\s+/g, " ").trim().slice(0, 120) ||
    undefined
  )
}

function getElementHref(element: HTMLElement | null) {
  if (element instanceof HTMLAnchorElement) {
    return sanitizeAnalyticsUrl(element.href)
  }

  return undefined
}

function getClickType(element: HTMLElement) {
  if (element instanceof HTMLAnchorElement) return "navigation"
  if (element instanceof HTMLButtonElement) return "action"
  if (element instanceof HTMLInputElement) return "form_action"
  if (element.getAttribute("role") === "link") return "navigation"
  if (element.getAttribute("role") === "button") return "action"

  return "custom"
}

function getLinkType(element: HTMLElement) {
  if (!(element instanceof HTMLAnchorElement)) return undefined

  const href = element.getAttribute("href") ?? ""
  if (href.startsWith("#")) return "anchor"
  if (href.startsWith("mailto:")) return "email"
  if (href.startsWith("tel:")) return "phone"

  try {
    const url = new URL(element.href)
    return url.origin === window.location.origin ? "internal" : "external"
  } catch {
    return undefined
  }
}

function getSelectedTextLength() {
  return window.getSelection()?.toString().trim().length ?? 0
}

export default function StoryblokClickTrackingClient() {
  useEffect(() => {
    function onClick(event: MouseEvent) {
      const trackingElement = findElement(event.target, trackingSelector)
      if (!trackingElement) return

      const interactiveElement = findElement(event.target, interactiveSelector)
      if (!interactiveElement || !trackingElement.contains(interactiveElement)) {
        return
      }

      const selectedTextLength = getSelectedTextLength()
      const clickType = getClickType(interactiveElement)

      sendGTMEventIfAnalyticsAllowed({
        event: "cms_component_click",
        cms_component: {
          tracking_id: trackingElement.dataset.gtmTrackingId,
          component: trackingElement.dataset.sbComponent,
          uid: trackingElement.dataset.sbUid,
          click_type: clickType,
          is_navigation_click: clickType === "navigation",
          link_type: getLinkType(interactiveElement),
          click_element: interactiveElement.tagName.toLowerCase(),
          click_text: getElementLabel(interactiveElement),
          click_url: getElementHref(interactiveElement),
          opens_in_new_tab: interactiveElement.getAttribute("target") === "_blank" || undefined,
          has_text_selection: selectedTextLength > 0 || undefined,
          selected_text_length: selectedTextLength || undefined,
          page_path: sanitizeAnalyticsPath(`${window.location.pathname}${window.location.search}${window.location.hash}`),
        },
      })
    }

    document.addEventListener("click", onClick)

    return () => document.removeEventListener("click", onClick)
  }, [])

  return null
}
